import {CheckboxSkeletonLoader, ResetFilters} from "components";
import {useAppSelector} from "hooks";
import {SearchInput} from "mapx-components";
import CheckboxList from "mapx-components/Inputs/CheckboxList";
import React, {useCallback, useMemo, useState} from "react";
import {
	countryOptionsPreCalculatedSelector,
	countryOptionsSelector,
} from "store/search/searchSelectors";

import {ContinentItem} from "./components";
import {getLocationExpandableData, TExpandedState} from "./helpers";
import {useLocationFilter} from "./hooks";
import LocationFilterTags from "./Tags";
import {LocationSearchFilterProps, TContinent, TCountry, TRegion, TState} from "./types";

/**
 * Deep filter function that searches through the entire location hierarchy
 * Supports searching: Continent -> Country -> Region -> State -> City
 * and also: Continent -> Country -> City (direct cities)
 */
const filterLocationData = (data: TContinent[], searchTerm: string): TContinent[] => {
	if (!searchTerm) return data;

	const lowerSearchTerm = searchTerm.toLowerCase();

	return data
		.map((continent) => {
			// Check if continent name matches
			const continentMatches = continent.name?.toLowerCase().includes(lowerSearchTerm);

			if (continentMatches) {
				// If continent matches, include all countries
				return continent;
			}

			// Filter countries
			const filteredCountries = continent.countries
				?.map((country) => {
					// Check if country name matches
					const countryMatches = country.name?.toLowerCase().includes(lowerSearchTerm);

					if (countryMatches) {
						// If country matches, include all regions/states/cities
						return country;
					}

					// Check for direct cities on country (Continent -> Country -> City structure)
					const matchingDirectCities = country.cities?.filter((city) =>
						city.name?.toLowerCase().includes(lowerSearchTerm),
					);

					// Check for regions -> states -> cities
					const filteredRegions = country.regions
						?.map((region) => {
							// Check if region name matches
							const regionMatches = region.name
								?.toLowerCase()
								.includes(lowerSearchTerm);

							if (regionMatches) {
								// If region matches, include all states
								return region;
							}

							// Filter states
							const filteredStates = region.states
								?.map((state) => {
									// Check if state name matches
									const stateMatches = state.name
										?.toLowerCase()
										.includes(lowerSearchTerm);

									if (stateMatches) {
										// If state matches, include all cities
										return state;
									}

									// Filter cities
									const filteredCities = state.cities?.filter((city) =>
										city.name?.toLowerCase().includes(lowerSearchTerm),
									);

									if (filteredCities && filteredCities.length > 0) {
										return {...state, cities: filteredCities};
									}

									return null;
								})
								.filter((state): state is TState => state !== null);

							if (filteredStates && filteredStates.length > 0) {
								return {...region, states: filteredStates};
							}

							return null;
						})
						.filter((region): region is TRegion => region !== null);

					// Check for direct states on country (Continent -> Country -> State -> City structure)
					const filteredDirectStates = country.states
						?.map((state) => {
							// Check if state name matches
							const stateMatches = state.name
								?.toLowerCase()
								.includes(lowerSearchTerm);

							if (stateMatches) {
								return state;
							}

							// Filter cities within state
							const filteredCities = state.cities?.filter((city) =>
								city.name?.toLowerCase().includes(lowerSearchTerm),
							);

							if (filteredCities && filteredCities.length > 0) {
								return {...state, cities: filteredCities};
							}

							return null;
						})
						.filter((state): state is TState => state !== null);

					// If any matches found in this country's hierarchy, include the country
					const hasRegionMatches = filteredRegions && filteredRegions.length > 0;
					const hasDirectStateMatches =
						filteredDirectStates && filteredDirectStates.length > 0;
					const hasDirectCityMatches =
						matchingDirectCities && matchingDirectCities.length > 0;

					if (hasRegionMatches || hasDirectStateMatches || hasDirectCityMatches) {
						return {
							...country,
							regions: hasRegionMatches ? filteredRegions : country.regions,
							states: hasDirectStateMatches ? filteredDirectStates : country.states,
							cities: hasDirectCityMatches ? matchingDirectCities : country.cities,
						};
					}

					return null;
				})
				.filter((country): country is TCountry => country !== null);

			if (filteredCountries && filteredCountries.length > 0) {
				return {...continent, countries: filteredCountries};
			}

			return null;
		})
		.filter((continent): continent is TContinent => continent !== null);
};

/**
 * LocationSearchFilter Component
 *
 * A hierarchical location filter that allows users to select:
 * - Continents (bulk select all countries)
 * - Countries
 * - Regions (within countries)
 * - States (within regions)
 * - Cities (within states)
 *
 * Supports cascading selection logic where selecting a parent
 * implicitly selects all children.
 */
const LocationSearchFilter: React.FC<LocationSearchFilterProps> = ({
	filterType = "candidates",
	handleResetClick,
	handleOnChange,
	handleBulkUpdate,
	selectedLocation = [],
	selectedCountryRegions = [],
	selectedRegionStates = [],
	selectedStateCities = [],
	disabled = false,
	handleRegionChange,
	handleStateChange,
	handleCityChange,
	handleTagClick,
	handleMoveLocationTag,
	selectedCountries,
	allSelectedCountriesCount = 0,
}) => {
	const [localSearchTerm, setLocalSearchTerm] = useState("");
	const [searchExpanded, setSearchExpanded] = useState<TExpandedState>({});

	// Get raw data directly from Redux for deep searching
	const countryOptionsPreCalculated = useAppSelector(countryOptionsPreCalculatedSelector);
	const countryOptions = useAppSelector(countryOptionsSelector);

	// Determine which raw data to use based on filter type
	const rawData = useMemo(
		() =>
			(filterType === "candidates" || filterType === "ap_candidates"
				? countryOptionsPreCalculated
				: countryOptions) as TContinent[],
		[filterType, countryOptionsPreCalculated, countryOptions],
	);

	const {
		// Data
		filteredData: hookFilteredData,
		loading,
		expanded: hookExpanded,

		// Derived state
		shouldRenderCountryRegions,
		shouldRenderTags,

		// Handlers
		onFilterChanged: hookOnFilterChanged,
		handleContinentChange,
		toggleExpandItem,
		toggleRegion,
		toggleState,
		toggleCity,

		// Selection checks
		hasChildren,
		// hasContinentSelected,
		isCountrySelectedCb,
		isCountryPartiallySelectedCb,
		isRegionPartiallySelectedCb,
		isStatePartiallySelectedCb,
		isCountryRegionSelectedCb,
		isStateSelectedCb,
		isCitySelectedCb,
		isContinentFullySelected,
	} = useLocationFilter({
		filterType,
		selectedLocation,
		selectedCountryRegions,
		selectedRegionStates,
		selectedStateCities,
		handleBulkUpdate,
		handleRegionChange,
		handleStateChange,
		handleCityChange,
		allSelectedCountriesCount,
	});

	// Apply deep filtering on the RAW data when searching
	const enhancedFilteredData = useMemo(() => {
		if (!localSearchTerm) {
			return hookFilteredData;
		}

		// Apply deep filtering on raw data (not hook's filtered data)
		return filterLocationData(rawData || [], localSearchTerm);
	}, [rawData, hookFilteredData, localSearchTerm]);

	// Combine expanded state from hook and search-triggered expansion
	const combinedExpanded = useMemo(() => {
		if (!localSearchTerm) {
			return hookExpanded;
		}

		// When searching, expand all matching items
		return {...hookExpanded, ...searchExpanded};
	}, [hookExpanded, searchExpanded, localSearchTerm]);

	// Custom search handler that applies deep filtering
	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const searchValue = e.target.value;
			setLocalSearchTerm(searchValue);

			// Also call the hook's filter for backwards compatibility
			hookOnFilterChanged(e);

			// Auto-expand all items when searching
			if (searchValue) {
				const filteredResult = filterLocationData(rawData || [], searchValue);
				const expandableData = getLocationExpandableData(filteredResult);
				setSearchExpanded(expandableData);
			} else {
				setSearchExpanded({});
			}
		},
		[hookOnFilterChanged, rawData],
	);

	const displayOverlay = useMemo(() => disabled, [disabled]);

	return (
		<div>
			{/* Search Input */}
			<SearchInput
				onChange={handleSearchChange}
				placeholder="Search for a region, state, city or country"
				type="text"
				isLoading={loading}
			/>

			{/* Reset Button */}
			<ResetFilters
				parentStyle={{color: "#5A5A5A", marginRight: 19}}
				onClick={handleResetClick}
				displayIcon
			>
				Clear Selection
			</ResetFilters>

			{/* Location Tree */}
			<CheckboxList displayOverlay={displayOverlay} overlayText="Adjusting search parameters">
				{enhancedFilteredData.map((continent) => {
					const isContinentSelected = isContinentFullySelected(continent);
					const isContinentPartiallySelected =
						!isContinentSelected &&
						Boolean(
							continent.countries?.some(
								(country) =>
									isCountrySelectedCb(country.id) ||
									isCountryPartiallySelectedCb(country),
							),
						);

					return (
						<ContinentItem
							key={continent.id}
							continent={continent}
							expanded={combinedExpanded}
							disabled={disabled}
							shouldRenderCountryRegions={shouldRenderCountryRegions}
							isSelected={isContinentSelected}
							isPartiallySelected={isContinentPartiallySelected}
							onToggle={() => handleContinentChange(continent)}
							onToggleExpand={() => toggleExpandItem(continent)}
							toggleExpandItem={toggleExpandItem}
							handleCountryChange={handleOnChange}
							toggleRegion={toggleRegion}
							toggleState={toggleState}
							toggleCity={toggleCity}
							hasChildren={hasChildren}
							isCountrySelected={isCountrySelectedCb}
							isCountryPartiallySelected={isCountryPartiallySelectedCb}
							isRegionSelected={isCountryRegionSelectedCb}
							isRegionPartiallySelected={isRegionPartiallySelectedCb}
							isStateSelected={isStateSelectedCb}
							isStatePartiallySelected={isStatePartiallySelectedCb}
							isCitySelected={isCitySelectedCb}
						/>
					);
				})}
			</CheckboxList>

			{/* Loading Skeleton */}
			{loading && <CheckboxSkeletonLoader />}

			{/* Location Tags */}
			{shouldRenderTags && (
				<div style={{marginTop: "10px"}} data-testid="LocationFilterTags">
					<LocationFilterTags
						label="Current"
						position="current"
						selectedCountries={selectedCountries?.current}
						handleTagClick={handleTagClick}
						handleMoveLocationTag={handleMoveLocationTag}
					/>

					<LocationFilterTags
						label="Previous"
						position="previous"
						selectedCountries={selectedCountries?.previous}
						handleTagClick={handleTagClick}
						handleMoveLocationTag={handleMoveLocationTag}
					/>

					<LocationFilterTags
						label="Any"
						position="any"
						selectedCountries={selectedCountries?.any}
						handleTagClick={handleTagClick}
						handleMoveLocationTag={handleMoveLocationTag}
					/>
				</div>
			)}
		</div>
	);
};

export default LocationSearchFilter;
